import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, Volume2, VolumeX, Radio, Calendar, Mail, Phone, MapPin, Send, Heart, Facebook, Instagram, Twitter, Music2 } from 'lucide-react';
import { mockData } from '../mockData';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';

const Home = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(75);
  const [feedbackForm, setFeedbackForm] = useState({ name: '', email: '', message: '' });
  const [gratitudeMessage, setGratitudeMessage] = useState('');
  const { toast } = useToast();

  const togglePlay = () => {
    setIsPlaying(!isPlaying);
    toast({
      title: isPlaying ? "Stream Paused" : "Now Playing Live",
      description: isPlaying ? "Stream paused" : "WVOD Power 93 - The Voice of the Diaspora",
    });
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  const handleFeedbackSubmit = (e) => {
    e.preventDefault();
    toast({
      title: "Feedback Received!",
      description: "Thank you for your feedback. We'll review it soon.",
    });
    setFeedbackForm({ name: '', email: '', message: '' });
  };

  const handleGratitudeSubmit = (e) => {
    e.preventDefault();
    if (gratitudeMessage.trim()) {
      toast({
        title: "Gratitude Shared!",
        description: "Your message has been added to our gratitude board.",
      });
      setGratitudeMessage('');
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-md border-b border-white/10">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center gap-3">
              <Radio className="w-8 h-8 text-mid-orange" />
              <div>
                <h1 className="text-2xl font-bold text-white">WVOD POWER 93</h1>
                <p className="text-xs text-mid-grey uppercase tracking-widest">Voice of the Diaspora</p>
              </div>
            </div>
            <nav className="hidden md:flex items-center gap-8">
              <a href="#about" className="nav-link text-white hover:text-mid-orange transition-colors">About</a>
              <a href="#schedule" className="nav-link text-white hover:text-mid-orange transition-colors">Schedule</a>
              <a href="#contact" className="nav-link text-white hover:text-mid-orange transition-colors">Contact</a>
              <a href="#gratitude" className="nav-link text-white hover:text-mid-orange transition-colors">Gratitude</a>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center pt-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-mid-orange rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-mid-purple rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative z-10 max-w-6xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-mid-orange/20 border border-mid-orange/30 rounded-full mb-8">
            <span className="w-2 h-2 bg-mid-orange rounded-full animate-pulse"></span>
            <span className="text-sm font-mono uppercase tracking-widest text-mid-orange">Live Now</span>
          </div>
          
          <h2 className="text-5xl md:text-7xl lg:text-8xl font-bold mb-12 leading-tight">
            The Ultimate Destination<br />
            <span className="text-mid-orange">For Music</span><br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-mid-orange via-mid-pink to-mid-purple">The Way It Used To Be</span>
          </h2>

          {/* Video Player Box */}
          <div className="max-w-4xl mx-auto mb-12">
            <Card className="bg-black border border-white/10 rounded-2xl overflow-hidden">
              <div className="aspect-video bg-black relative">
                <video 
                  controls 
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="w-full h-full rounded-t-2xl"
                  poster=""
                  preload="auto"
                >
                  <source src="https://video.wixstatic.com/video/4bfe2c_db0d778e179a423eb2a97bb732047c6d/1080p/mp4/file.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
              <div className="p-4 bg-white/5">
                <p className="text-white text-sm font-bold">WVOD Power 93 - Station Promo</p>
              </div>
            </Card>
          </div>
          
          <p className="text-xl md:text-2xl text-mid-grey max-w-3xl mx-auto mb-12 leading-relaxed">
            {mockData.hero.description}
          </p>

          {/* Live Radio Player - Radio.co Embed */}
          <div className="max-w-2xl mx-auto">
            <Card className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-2xl overflow-hidden">
              <iframe 
                src="https://s4.radio.co/s5f6084ed4/listen" 
                frameBorder="0" 
                scrolling="no" 
                height="200" 
                width="100%"
                title="WVOD Power 93 Live Stream"
                className="w-full"
              ></iframe>
            </Card>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 px-4 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Your Musical Journey Awaits</h2>
          </div>

          {/* Text Content */}
          <div className="text-center max-w-4xl mx-auto mb-12">
            <p className="text-lg md:text-xl leading-relaxed text-white/90">
              Join <span className="text-mid-orange font-bold">DJ MG</span>, <span className="text-mid-orange font-bold">C Fresh</span>, <span className="text-mid-orange font-bold">Infamous SoLJah sound</span> and the rest of the WVOD POWER 93 team as we take you on a musical journey through the ages.
            </p>
            <p className="text-lg md:text-xl leading-relaxed text-white/90 mt-4">
              From the golden era to modern hits, we curate the perfect blend of tracks that celebrate the rich tapestry of music while providing top of the hour news, informative shows on the history of the diaspora, entertainment and daily devotions to help you have a good day on purpose.
            </p>
            <p className="text-lg md:text-xl leading-relaxed text-mid-orange font-bold mt-4">
              Listen now to embark on this melodic adventure.
            </p>
          </div>

          <Card className="bg-gradient-to-br from-mid-purple/20 to-mid-orange/20 border border-white/10 p-8 rounded-2xl">
            <div className="flex flex-col md:flex-row items-center justify-center gap-8">
              <img 
                src="https://static.wixstatic.com/media/4bfe2c_5a792627a08d495cb304086fc0206377~mv2.png" 
                alt="WVOD Power 93 Logo" 
                className="w-96 h-96 md:w-[28rem] md:h-[28rem] object-contain flex-shrink-0"
              />
              <p className="text-lg md:text-xl leading-relaxed text-center md:text-left text-white/90">
                <span className="text-mid-orange font-bold">Music Powered by INFAMOUS SOLJAH Sound</span> - Spinning the best and the rest in Hip Hop, House, RnB, Reggae, Dancehall, Calypso, Soca, Reggaeton and Afrobeats
              </p>
            </div>
          </Card>
        </div>
      </section>

      {/* Throwback of the Week Section */}
      <section className="py-24 px-4 bg-black">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Throwback of the Week</h2>
            <p className="text-xl text-mid-grey">Relive the classics that defined an era</p>
          </div>

          <Card className="bg-gradient-to-br from-mid-purple/10 to-mid-orange/10 border border-white/10 p-8 rounded-2xl">
            <div className="flex items-center gap-6 mb-6">
              <div className="w-32 h-32 rounded-xl bg-gradient-to-br from-mid-orange to-mid-pink flex items-center justify-center flex-shrink-0 shadow-lg shadow-mid-orange/30">
                <Music2 className="w-16 h-16 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-white mb-2">Featured Track</h3>
                <p className="text-lg text-mid-grey mb-1">Classic Hit from the Golden Era</p>
                <p className="text-sm text-mid-orange font-mono uppercase tracking-widest">This Week's Selection</p>
              </div>
            </div>

            {/* Audio Player Controls */}
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
              <div className="flex items-center gap-4 mb-4">
                <button className="w-14 h-14 rounded-full bg-gradient-to-r from-mid-orange to-mid-pink hover:from-mid-orange/80 hover:to-mid-pink/80 flex items-center justify-center transition-all transform hover:scale-105 shadow-lg shadow-mid-orange/50 flex-shrink-0">
                  <Play className="w-7 h-7 text-white ml-1" />
                </button>
                
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-white/60">0:00</span>
                    <span className="text-sm text-white/60">3:45</span>
                  </div>
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden cursor-pointer">
                    <div className="h-full bg-gradient-to-r from-mid-orange to-mid-pink w-0 rounded-full transition-all"></div>
                  </div>
                </div>

                <button className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all">
                  <Volume2 className="w-5 h-5 text-white" />
                </button>
              </div>

              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-4">
                  <button className="text-white/60 hover:text-white transition-colors">
                    <span className="text-xs uppercase tracking-wider">Download</span>
                  </button>
                  <button className="text-white/60 hover:text-white transition-colors">
                    <Heart className="w-4 h-4 inline mr-1" />
                    <span className="text-xs uppercase tracking-wider">Save</span>
                  </button>
                </div>
                <span className="text-white/40 text-xs">MP3 • 320kbps</span>
              </div>
            </div>

            <div className="mt-6 p-4 bg-mid-orange/10 border border-mid-orange/20 rounded-lg">
              <p className="text-sm text-white/80 text-center">
                🎵 Tune in every week for a new throwback classic that takes you down memory lane
              </p>
            </div>
          </Card>
        </div>
      </section>

      {/* Featured Video Section */}
      <section className="py-24 px-4 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Featured Video</h2>
            <p className="text-xl text-mid-grey">Watch the latest from WVOD Power 93</p>
          </div>

          <Card className="bg-black border border-white/10 rounded-2xl overflow-hidden">
            <div className="aspect-video bg-gradient-to-br from-mid-purple/20 via-mid-orange/20 to-mid-pink/20 relative group">
              {/* Video Thumbnail Overlay */}
              <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-all"></div>
              
              {/* Play Button */}
              <div className="absolute inset-0 flex items-center justify-center">
                <button className="w-24 h-24 rounded-full bg-gradient-to-r from-mid-orange to-mid-pink hover:from-mid-orange/80 hover:to-mid-pink/80 flex items-center justify-center transition-all transform hover:scale-110 shadow-2xl shadow-mid-orange/50 group-hover:w-28 group-hover:h-28">
                  <Play className="w-12 h-12 text-white ml-1" />
                </button>
              </div>

              {/* Video Title Overlay */}
              <div className="absolute top-0 left-0 right-0 p-6 bg-gradient-to-b from-black/80 to-transparent">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                  <span className="text-sm font-mono uppercase tracking-widest text-red-500">Live</span>
                </div>
                <h3 className="text-2xl font-bold text-white">WVOD Power 93 Live Stream</h3>
              </div>

              {/* Video Controls Bar */}
              <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/90 to-transparent">
                <div className="space-y-3">
                  {/* Progress Bar */}
                  <div className="w-full h-1 bg-white/20 rounded-full cursor-pointer">
                    <div className="h-full bg-gradient-to-r from-mid-orange to-mid-pink rounded-full w-1/3"></div>
                  </div>
                  
                  {/* Control Buttons */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <button className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all">
                        <Volume2 className="w-4 h-4 text-white" />
                      </button>
                      <span className="text-sm text-white/80">1:45 / 5:30</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button className="px-3 py-1 rounded-full bg-white/10 hover:bg-white/20 text-xs text-white uppercase tracking-wider transition-all">
                        HD
                      </button>
                      <button className="px-3 py-1 rounded-full bg-white/10 hover:bg-white/20 text-xs text-white uppercase tracking-wider transition-all">
                        Fullscreen
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Video Description */}
            <div className="p-6 bg-white/5">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-mid-orange to-mid-pink flex items-center justify-center flex-shrink-0">
                  <Radio className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h4 className="text-lg font-bold text-white mb-2">Behind the Scenes at WVOD Power 93</h4>
                  <p className="text-sm text-mid-grey mb-3">
                    Get an exclusive look at how we bring you the best music, news, and entertainment. Meet the team, see our studio, and discover what makes WVOD Power 93 the voice of the diaspora.
                  </p>
                  <div className="flex items-center gap-4 text-xs text-white/60">
                    <span>👁️ 12.5K views</span>
                    <span>•</span>
                    <span>2 days ago</span>
                    <span>•</span>
                    <span className="text-mid-orange">#WVODPower93 #BehindTheScenes</span>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Event Calendar Section */}
      <section className="py-24 px-4 bg-black">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Upcoming Events</h2>
            <p className="text-xl text-mid-grey">Join us for special shows, live performances, and community events</p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Event Cards */}
            <Card className="bg-gradient-to-br from-mid-orange/20 to-mid-pink/20 border border-white/10 p-6 rounded-2xl hover:scale-105 transition-all group cursor-pointer">
              <div className="flex items-start gap-4 mb-4">
                <div className="bg-gradient-to-br from-mid-orange to-mid-pink p-4 rounded-xl flex-shrink-0">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-white">15</div>
                    <div className="text-xs uppercase tracking-wider text-white/80">Jan</div>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-white mb-2 group-hover:text-mid-orange transition-colors">Live DJ Set: C Fresh</h3>
                  <p className="text-sm text-mid-grey mb-2">Friday • 8:00 PM - 11:00 PM</p>
                  <div className="flex items-center gap-2">
                    <span className="px-3 py-1 bg-mid-orange/20 border border-mid-orange/30 rounded-full text-xs text-mid-orange uppercase tracking-wider">Live</span>
                    <span className="px-3 py-1 bg-white/10 border border-white/20 rounded-full text-xs text-white uppercase tracking-wider">Hip Hop</span>
                  </div>
                </div>
              </div>
              <p className="text-sm text-white/70 mb-4">
                Experience an unforgettable night of classic hip hop hits mixed live by C Fresh. Special guest appearances and giveaways!
              </p>
              <button className="w-full py-2 bg-gradient-to-r from-mid-orange to-mid-pink hover:from-mid-orange/80 hover:to-mid-pink/80 rounded-full text-white font-mono text-xs uppercase tracking-widest transition-all">
                Add to Calendar
              </button>
            </Card>

            <Card className="bg-gradient-to-br from-mid-purple/20 to-mid-blue/20 border border-white/10 p-6 rounded-2xl hover:scale-105 transition-all group cursor-pointer">
              <div className="flex items-start gap-4 mb-4">
                <div className="bg-gradient-to-br from-mid-purple to-mid-blue p-4 rounded-xl flex-shrink-0">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-white">22</div>
                    <div className="text-xs uppercase tracking-wider text-white/80">Jan</div>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-white mb-2 group-hover:text-mid-purple transition-colors">Diaspora Stories Hour</h3>
                  <p className="text-sm text-mid-grey mb-2">Tuesday • 7:00 PM - 8:00 PM</p>
                  <div className="flex items-center gap-2">
                    <span className="px-3 py-1 bg-mid-purple/20 border border-mid-purple/30 rounded-full text-xs text-mid-purple uppercase tracking-wider">Special</span>
                    <span className="px-3 py-1 bg-white/10 border border-white/20 rounded-full text-xs text-white uppercase tracking-wider">Talk Show</span>
                  </div>
                </div>
              </div>
              <p className="text-sm text-white/70 mb-4">
                Join us for powerful stories from the diaspora community. Guest speakers share their journey, heritage, and cultural impact.
              </p>
              <button className="w-full py-2 bg-gradient-to-r from-mid-purple to-mid-blue hover:from-mid-purple/80 hover:to-mid-blue/80 rounded-full text-white font-mono text-xs uppercase tracking-widest transition-all">
                Add to Calendar
              </button>
            </Card>

            <Card className="bg-gradient-to-br from-mid-green/20 to-mid-yellow/20 border border-white/10 p-6 rounded-2xl hover:scale-105 transition-all group cursor-pointer">
              <div className="flex items-start gap-4 mb-4">
                <div className="bg-gradient-to-br from-mid-green to-mid-yellow p-4 rounded-xl flex-shrink-0">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-white">28</div>
                    <div className="text-xs uppercase tracking-wider text-white/80">Jan</div>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-white mb-2 group-hover:text-mid-green transition-colors">Reggae Sunday Special</h3>
                  <p className="text-sm text-mid-grey mb-2">Sunday • 9:00 AM - 12:00 PM</p>
                  <div className="flex items-center gap-2">
                    <span className="px-3 py-1 bg-mid-green/20 border border-mid-green/30 rounded-full text-xs text-mid-green uppercase tracking-wider">Featured</span>
                    <span className="px-3 py-1 bg-white/10 border border-white/20 rounded-full text-xs text-white uppercase tracking-wider">Reggae</span>
                  </div>
                </div>
              </div>
              <p className="text-sm text-white/70 mb-4">
                Start your Sunday with the smoothest reggae vibes. Featuring classic Bob Marley, modern dancehall, and everything in between.
              </p>
              <button className="w-full py-2 bg-gradient-to-r from-mid-green to-mid-yellow hover:from-mid-green/80 hover:to-mid-yellow/80 rounded-full text-white font-mono text-xs uppercase tracking-widest transition-all">
                Add to Calendar
              </button>
            </Card>
          </div>

          <div className="mt-12 text-center">
            <Card className="inline-block bg-white/5 border border-white/10 p-8 rounded-2xl">
              <Calendar className="w-12 h-12 mx-auto mb-4 text-mid-orange" />
              <h3 className="text-xl font-bold text-white mb-2">Never Miss an Event</h3>
              <p className="text-mid-grey mb-4 max-w-md">Subscribe to our event calendar and get notifications for all upcoming shows and special broadcasts</p>
              <button className="px-8 py-3 bg-gradient-to-r from-mid-orange to-mid-pink hover:from-mid-orange/80 hover:to-mid-pink/80 rounded-full text-white font-mono text-xs uppercase tracking-widest transition-all">
                Subscribe to Calendar
              </button>
            </Card>
          </div>
        </div>
      </section>

      {/* Schedule Section */}
      <section id="schedule" className="py-24 px-4 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Weekly Schedule</h2>
            <p className="text-xl text-mid-grey">Tune in to your favorite shows</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {mockData.schedule.map((show, index) => (
              <Card key={index} className="bg-white/5 border border-white/10 p-6 rounded-2xl hover:bg-white/10 transition-all group">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-mid-orange to-mid-purple flex items-center justify-center flex-shrink-0 group-hover:rotate-6 transition-transform">
                    <Calendar className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold mb-2">{show.name}</h3>
                    <p className="text-mid-orange font-mono text-sm mb-2">{show.time}</p>
                    <p className="text-mid-grey">{show.description}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* News - Sports - Entertainment Section */}
      <section className="py-24 px-4 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">News - Sports - Entertainment</h2>
            <p className="text-xl text-mid-grey">Stay updated with the latest from the Caribbean and beyond</p>
          </div>

          {/* News Feed Embed */}
          <Card className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden mb-8">
            <div className="p-6">
              <div className="text-center text-mid-grey mb-4">
                <p className="text-sm">News feed powered by your RSS/API source</p>
              </div>
              {/* Add your news feed widget/embed here */}
              <div className="space-y-6">
                {/* Sample news items - replace with actual feed */}
                <div className="border-b border-white/10 pb-6">
                  <img src="https://via.placeholder.com/800x400" alt="News" className="w-full h-48 object-cover rounded-lg mb-4" />
                  <h3 className="text-xl font-bold text-white mb-2">Caribbean Music Awards 2025</h3>
                  <p className="text-mid-grey text-sm mb-2">January 8, 2026</p>
                  <p className="text-white/80">Latest highlights from the Caribbean Music Awards celebrating the best in reggae, dancehall, and soca...</p>
                </div>
                <div className="border-b border-white/10 pb-6">
                  <img src="https://via.placeholder.com/800x400" alt="News" className="w-full h-48 object-cover rounded-lg mb-4" />
                  <h3 className="text-xl font-bold text-white mb-2">Dancehall Legends Tour Announced</h3>
                  <p className="text-mid-grey text-sm mb-2">January 7, 2026</p>
                  <p className="text-white/80">Iconic artists set to perform across North America this summer...</p>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Culture Section with YouTube Videos */}
      <section className="py-24 px-4 bg-black">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Culture</h2>
            <p className="text-xl text-mid-grey">Experience the vibrant culture through music videos</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* YouTube Video 1 */}
            <Card className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
              <div className="aspect-video">
                <iframe 
                  width="100%" 
                  height="100%" 
                  src="https://www.youtube.com/embed/Xc9YqzC-hEs" 
                  title="Baby Cham - Hustling In My Blood"
                  frameBorder="0" 
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                  allowFullScreen
                ></iframe>
              </div>
              <div className="p-4">
                <h3 className="text-lg font-bold text-white mb-2">Baby Cham - Hustling In My Blood</h3>
                <p className="text-sm text-mid-grey">Featuring Stephen Marley & Damian "Jr. Gong" Marley</p>
              </div>
            </Card>

            {/* YouTube Video 2 - Add your own */}
            <Card className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
              <div className="aspect-video bg-gradient-to-br from-mid-purple/20 to-mid-orange/20 flex items-center justify-center">
                <div className="text-center">
                  <Music2 className="w-16 h-16 text-white mx-auto mb-4" />
                  <p className="text-white">Add Your Featured Video</p>
                  <p className="text-sm text-mid-grey mt-2">Embed YouTube video URL here</p>
                </div>
              </div>
              <div className="p-4">
                <h3 className="text-lg font-bold text-white mb-2">Featured Video</h3>
                <p className="text-sm text-mid-grey">Add your video description here</p>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Weather Widget Section */}
      <section className="py-24 px-4 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Weather</h2>
            <p className="text-xl text-mid-grey">Current conditions in Toronto, ON</p>
          </div>

          <Card className="bg-white/5 border border-white/10 p-8 rounded-2xl">
            {/* Weather Widget Placeholder */}
            <div className="text-center">
              <div className="inline-flex items-center gap-4 mb-6">
                <div className="w-24 h-24 bg-gradient-to-br from-mid-orange to-mid-pink rounded-full flex items-center justify-center">
                  <Calendar className="w-12 h-12 text-white" />
                </div>
                <div className="text-left">
                  <h3 className="text-3xl font-bold text-white">Toronto, ON</h3>
                  <p className="text-xl text-mid-grey">Canada</p>
                </div>
              </div>
              <p className="text-sm text-mid-grey mb-4">Add your weather widget embed here</p>
              <p className="text-xs text-white/40">You can use services like OpenWeatherMap, Weather.com, or POWR Weather Widget</p>
            </div>
          </Card>
        </div>
      </section>

      {/* Gratitude Board */}
      <section id="gratitude" className="py-24 px-4 bg-black">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Gratitude Board</h2>
            <p className="text-xl text-mid-grey">Share what you're grateful for today</p>
          </div>

          <Card className="bg-white/5 border border-white/10 p-8 rounded-2xl mb-8">
            <form onSubmit={handleGratitudeSubmit} className="space-y-4">
              <Textarea
                placeholder="I'm grateful for..."
                value={gratitudeMessage}
                onChange={(e) => setGratitudeMessage(e.target.value)}
                className="bg-white/5 border-white/10 text-white placeholder:text-mid-grey min-h-32"
              />
              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-mid-orange to-mid-pink hover:from-mid-orange/80 hover:to-mid-pink/80 text-white font-mono uppercase tracking-widest rounded-full py-6"
              >
                <Heart className="w-5 h-5 mr-2" />
                Share Gratitude
              </Button>
            </form>
          </Card>

          <div className="grid md:grid-cols-2 gap-4">
            {mockData.gratitude.map((message, index) => (
              <Card key={index} className="bg-white/5 border border-white/10 p-6 rounded-xl">
                <p className="text-white mb-3 italic">"{message.message}"</p>
                <p className="text-sm text-mid-grey">- {message.author}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Feedback Section */}
      <section className="py-24 px-4 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Tell Us What You Think</h2>
            <p className="text-xl text-mid-grey">
              Have an idea you want to share on how we can make WVOD Power 93 better? Disagree with something you heard? Let us know!
            </p>
          </div>

          <Card className="bg-white/5 border border-white/10 p-8 rounded-2xl">
            <form onSubmit={handleFeedbackSubmit} className="space-y-6">
              <div>
                <Input
                  type="text"
                  placeholder="Your Name"
                  value={feedbackForm.name}
                  onChange={(e) => setFeedbackForm({ ...feedbackForm, name: e.target.value })}
                  className="bg-white/5 border-white/10 text-white placeholder:text-mid-grey"
                  required
                />
              </div>
              <div>
                <Input
                  type="email"
                  placeholder="Your Email"
                  value={feedbackForm.email}
                  onChange={(e) => setFeedbackForm({ ...feedbackForm, email: e.target.value })}
                  className="bg-white/5 border-white/10 text-white placeholder:text-mid-grey"
                  required
                />
              </div>
              <div>
                <Textarea
                  placeholder="Your Feedback or Suggestions"
                  value={feedbackForm.message}
                  onChange={(e) => setFeedbackForm({ ...feedbackForm, message: e.target.value })}
                  className="bg-white/5 border-white/10 text-white placeholder:text-mid-grey min-h-40"
                  required
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-mid-orange to-mid-pink hover:from-mid-orange/80 hover:to-mid-pink/80 text-white font-mono uppercase tracking-widest rounded-full py-6"
              >
                <Send className="w-5 h-5 mr-2" />
                Send Feedback
              </Button>
            </form>
          </Card>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 px-4 bg-black">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Get In Touch</h2>
            <p className="text-xl text-mid-grey">Reach out to the WVOD Power 93 team</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="bg-white/5 border border-white/10 p-8 rounded-2xl text-center hover:bg-white/10 transition-all">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-mid-orange to-mid-pink flex items-center justify-center">
                <Mail className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-2">Email</h3>
              <p className="text-mid-grey">{mockData.contact.email}</p>
            </Card>

            <Card className="bg-white/5 border border-white/10 p-8 rounded-2xl text-center hover:bg-white/10 transition-all">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-mid-purple to-mid-blue flex items-center justify-center">
                <Phone className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-2">Phone</h3>
              <p className="text-mid-grey">{mockData.contact.phone}</p>
            </Card>

            <Card className="bg-white/5 border border-white/10 p-8 rounded-2xl text-center hover:bg-white/10 transition-all">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-mid-pink to-mid-purple flex items-center justify-center">
                <MapPin className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-2">Location</h3>
              <p className="text-mid-grey">{mockData.contact.location}</p>
            </Card>
          </div>

          <div className="mt-12 text-center">
            <p className="text-mid-grey mb-6">Follow us on social media</p>
            <div className="flex justify-center gap-4">
              <a href="#" className="w-12 h-12 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center transition-all hover:scale-110">
                <Facebook className="w-6 h-6 text-white" />
              </a>
              <a href="#" className="w-12 h-12 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center transition-all hover:scale-110">
                <Instagram className="w-6 h-6 text-white" />
              </a>
              <a href="#" className="w-12 h-12 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center transition-all hover:scale-110">
                <Twitter className="w-6 h-6 text-white" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 bg-black border-t border-white/10">
        <div className="max-w-6xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Radio className="w-8 h-8 text-mid-orange" />
            <h3 className="text-2xl font-bold">WVOD POWER 93</h3>
          </div>
          <p className="text-mid-grey mb-4">The Voice of the Diaspora</p>
          <p className="text-sm text-mid-grey">
            © 2025 WVOD Power 93. All rights reserved. | Music the way it used to be.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Home;